/**
 * Itinerary panel UI configuration for itinerary list surfaces.
 */

import type { PanelResizeProfile, PanelResizeProfileId, PanelSnapTier } from "../components/ItinerariesPlanner/panelResize/panelResize.types";

/** Itinerary list card background rendering mode. */
export type ItineraryPanelCardBackgroundStyle = "gradient" | "solid";

/**
 * Itinerary panel layout and itinerary list presentation settings.
 */
export type ItineraryPanelConfiguration = {

    /**
     * Itinerary card background in the itinerary list.
     * - `gradient`: layered vertical gradient with a subtle brand accent on top.
     * - `solid`: flat near-opaque overlay fill (`overlayCardBackgroundStrong`) so rows
     *   stay visually consistent regardless of underlay (map vs sibling cards).
     */
    "itineraryCardBackground": ItineraryPanelCardBackgroundStyle;

    /**
     * Temporary UI-only fallback price (CHF) for the itinerary-details chrome badge while pricing APIs
     * are unavailable. Set to `null` to disable. Remove once pricing is restored.
     */
    "tempFakeChromePriceChf": number | null;

    /** Global snap anchor (vh) for the `collapsed` tier. */
    "panelMinHeightVh": number;

    /** Global snap anchor (vh) for the `expanded` tier. */
    "panelMaxHeightVh": number;

    /** Global minimum meaningful live drag height delta (vh). */
    "dragHeightUpdateEpsilonVh": number;

    /**
     * Breathing room (px) between the floating action row (GPS + grouped FAB) and the
     * maximized panel top. Keeps the panel just below the localize / settings buttons.
     */
    "panelMaximizedTopGapPx": number;

    /**
     * When `false`, saved-list category filter tabs are hidden in saved-itinerary mode.
     */
    "enableSavedItineraryListFilters": boolean;

    /** Chrome row height (px) for the saved-itinerary list (title + close + drag handle). */
    "savedListChromeRowHeightPx": number;
};

export const ITINERARY_PANEL: ItineraryPanelConfiguration = {
    "itineraryCardBackground":         "solid",
    "tempFakeChromePriceChf":          null,
    "panelMinHeightVh":                22,
    "panelMaxHeightVh":                50,
    "dragHeightUpdateEpsilonVh":       0.05,
    "panelMaximizedTopGapPx":          12,
    "enableSavedItineraryListFilters": false,
    "savedListChromeRowHeightPx":      42,
};

const SHARED_SNAP: Pick<PanelResizeProfile, "snapCollapsedVh" | "snapExpandedVh" | "defaultSnapTier" | "dragHeightUpdateEpsilonVh"> = {
    "snapCollapsedVh":           ITINERARY_PANEL.panelMinHeightVh,
    "snapExpandedVh":            ITINERARY_PANEL.panelMaxHeightVh,
    "defaultSnapTier":           "expanded",
    "dragHeightUpdateEpsilonVh": ITINERARY_PANEL.dragHeightUpdateEpsilonVh,
};

const ALL_SNAP_TIERS = [ "collapsed", "expanded", "maximized" ] as const satisfies readonly PanelSnapTier[];

const EXPANDED_AND_MAXIMIZED_SNAP_TIERS = [ "expanded", "maximized" ] as const satisfies readonly PanelSnapTier[];

/**
 * Resize profiles for each surface that can host a {@link PanelResizeHandle}.
 */
export const PANEL_RESIZE_PROFILES: Record<PanelResizeProfileId, PanelResizeProfile> = {
    "itineraryDetails": {
        ...SHARED_SNAP,
        "profileId":                 "itineraryDetails",
        "chromeRowHeightPx":         68,
        "listContentHeightOffsetVh": 5,
        "allowedSnapTiers":          ALL_SNAP_TIERS,
    },
    "itineraryList": {
        ...SHARED_SNAP,
        "profileId":                 "itineraryList",
        "chromeRowHeightPx":         ITINERARY_PANEL.savedListChromeRowHeightPx,
        "listContentHeightOffsetVh": 5,
        "allowedSnapTiers":          EXPANDED_AND_MAXIMIZED_SNAP_TIERS,
    },
    "itineraryListSaved": {
        ...SHARED_SNAP,
        "profileId":                 "itineraryListSaved",
        "chromeRowHeightPx":         ITINERARY_PANEL.savedListChromeRowHeightPx,
        "listContentHeightOffsetVh": 5,
        "allowedSnapTiers":          EXPANDED_AND_MAXIMIZED_SNAP_TIERS,
    },
};

export type { PanelResizeProfile, PanelResizeProfileId, PanelSnapTier };
